import { useEffect, useMemo, useState } from 'react';
import { workflowApi } from '../services/api';
import { useConfigStore } from '../store/useConfigStore';
import type { StatusConfig } from '../types';

export interface WorkflowState {
  id: string;
  label: string;
  color: string;
  order: number;
  category: 'todo' | 'in_progress' | 'done' | 'cancelled';
  wipLimit: number;
  slaHours: number;
  isInitial: boolean;
  isTerminal: boolean;
  requiredFields: string[];
}

export interface ResolvedWorkflow {
  _id: string;
  name: string;
  version: number;
  entityType: string;
  states: WorkflowState[];
  transitions: { id: string; from: string[]; to: string }[];
}

/** Columns for a board, from the workflow that governs it.
 *
 *  Falls back to the global `config.statuses` list when no workflow resolves —
 *  a fresh instance where the seed has not been run, or an offline API. Without
 *  that fallback the board would render no columns at all, which is worse than
 *  rendering the pre-workflow ones.
 *
 *  `workItemType` should be passed only when the board is showing exactly one
 *  type; a project may bind different workflows per type, and asking for the
 *  wrong one produces misleading columns.
 */
export function useResolvedWorkflow(projectId?: string, workItemType?: string) {
  const { config } = useConfigStore();
  const [workflow, setWorkflow] = useState<ResolvedWorkflow | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    workflowApi
      .resolve({ projectId, workItemType, entityType: 'workitem' })
      .then((w) => { if (!cancelled) setWorkflow(w); })
      .catch(() => { if (!cancelled) setWorkflow(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId, workItemType]);

  const columns = useMemo<WorkflowState[]>(() => {
    if (workflow?.states?.length) {
      return [...workflow.states].sort((a, b) => a.order - b.order);
    }
    // Pre-workflow shape, mapped into the same structure so callers need no
    // branching. Categories are unknown here, so nothing claims to be done.
    return [...(config.statuses as StatusConfig[])]
      .sort((a, b) => a.order - b.order)
      .map((s, index) => ({
        id: s.id,
        label: s.label,
        color: s.color,
        order: s.order ?? index,
        category: 'todo' as const,
        wipLimit: 0,
        slaHours: 0,
        isInitial: index === 0,
        isTerminal: false,
        requiredFields: [],
      }));
  }, [workflow, config.statuses]);

  /** Legal targets from a state, or null when unknown (no workflow loaded). */
  const allowedFrom = useMemo(() => {
    if (!workflow?.transitions?.length) return null;
    return (from: string): string[] => {
      const targets = new Set<string>();
      workflow.transitions.forEach((t) => {
        const sources = t.from || [];
        if (sources.includes('*') || sources.includes(from)) targets.add(t.to);
      });
      return [...targets];
    };
  }, [workflow]);

  return { workflow, columns, allowedFrom, loading, isFallback: !workflow };
}
